import { useState } from 'react';
import { Button } from '@/shared/ui';
import type { JobStatus } from '@/entities/job';
import { JOB_KIND_LABEL } from '@/entities/job';
import { formatUsd } from '@/shared/pricing';
import { cn } from '@/shared/lib/cn';
import { jobScheduler } from '../scheduler';
import type { JobNode } from './useJobsTree';

const STATUS_TEXT: Record<JobStatus, string> = {
  pending: 'text-muted-foreground',
  running: 'text-amber-300',
  succeeded: 'text-emerald-400',
  failed: 'text-destructive',
  canceled: 'text-muted-foreground',
  interrupted: 'text-orange-300',
};

const STATUS_BAR: Record<JobStatus, string> = {
  pending: 'bg-muted',
  running: 'bg-amber-300',
  succeeded: 'bg-emerald-400/60',
  failed: 'bg-destructive',
  canceled: 'bg-muted',
  interrupted: 'bg-orange-300',
};

const STATUS_GLYPH: Record<JobStatus, string> = {
  pending: '○',
  running: '●',
  succeeded: '✓',
  failed: '✕',
  canceled: '⊘',
  interrupted: '⏸',
};

function countDescendants(node: JobNode): Record<JobStatus, number> {
  const acc: Record<JobStatus, number> = {
    pending: 0,
    running: 0,
    succeeded: 0,
    failed: 0,
    canceled: 0,
    interrupted: 0,
  };
  const walk = (n: JobNode) => {
    for (const c of n.children) {
      acc[c.job.status] += 1;
      walk(c);
    }
  };
  walk(node);
  return acc;
}

function formatRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString();
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs === 0 ? `${m}m` : `${m}m ${rs}s`;
}

export function JobTreeNode({
  node,
  depth = 0,
  isLeaf = false,
}: {
  node: JobNode;
  depth?: number;
  isLeaf?: boolean;
}) {
  const { job, children } = node;
  const [open, setOpen] = useState(false);
  const hasChildren = children.length > 0;
  const counts = hasChildren ? countDescendants(node) : null;
  const total = counts
    ? counts.pending +
      counts.running +
      counts.succeeded +
      counts.failed +
      counts.canceled +
      counts.interrupted
    : 0;
  const progressPct = total > 0 ? Math.round((counts!.succeeded / total) * 100) : 0;

  const canCancel = job.status === 'pending' || job.status === 'running';
  const canRetry =
    job.status === 'failed' || job.status === 'canceled' || job.status === 'interrupted';

  const isRunning = job.status === 'running';
  const isFailed = job.status === 'failed';
  const isDone = job.status === 'succeeded';

  const elapsedMs =
    job.startedAt !== undefined
      ? (job.completedAt ?? Date.now()) - job.startedAt
      : undefined;

  const errorExcerpt = job.error ? job.error.slice(0, 120) : '';

  return (
    <div className="text-sm">
      <div
        className={cn(
          'group relative flex items-stretch gap-2 overflow-hidden rounded border border-transparent transition-colors',
          'hover:border-border hover:bg-muted/30',
          isFailed && 'bg-destructive/5',
          isRunning && 'bg-amber-300/5',
        )}
      >
        <span
          aria-hidden
          className={cn('w-[3px] shrink-0', STATUS_BAR[job.status])}
        />
        <div
          className="flex min-w-0 flex-1 flex-col gap-0.5 py-1.5 pr-2"
          style={{ paddingLeft: `${0.25 + depth * 0.875}rem` }}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={open ? 'Collapse' : 'Expand'}
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
                !hasChildren && 'invisible',
              )}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? '▼' : '▶'}
            </button>
            <span
              aria-hidden
              className={cn(
                'shrink-0 text-[11px] leading-none',
                STATUS_TEXT[job.status],
                isRunning && 'animate-pulse',
              )}
              title={job.status}
            >
              {STATUS_GLYPH[job.status]}
            </span>
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                isDone ? 'text-foreground/80' : 'font-medium',
              )}
            >
              {job.label}
            </span>
            {job.actualCostUsd > 0 ? (
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatUsd(job.actualCostUsd)}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2 pl-5 text-xs text-muted-foreground">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
              {/* Active state: progress + elapsed */}
              {isRunning && counts && total > 0 ? (
                <>
                  <span>
                    {counts.succeeded}/{total}
                  </span>
                  {counts.running > 0 ? (
                    <span className="text-amber-300">· {counts.running} running</span>
                  ) : null}
                  {elapsedMs !== undefined ? <span>· {formatElapsed(elapsedMs)}</span> : null}
                </>
              ) : null}

              {/* Failed state: counts + error excerpt */}
              {isFailed ? (
                <>
                  {counts && total > 0 ? (
                    <span>
                      {counts.succeeded}/{total} done
                      {counts.failed > 0 ? (
                        <span className="text-destructive"> · {counts.failed} failed</span>
                      ) : null}
                    </span>
                  ) : null}
                  {errorExcerpt ? (
                    <span className="truncate text-destructive/90" title={job.error}>
                      {errorExcerpt}
                    </span>
                  ) : null}
                </>
              ) : null}

              {/* Done state: brief summary + relative time */}
              {isDone ? (
                <>
                  {counts && total > 0 ? (
                    <span>
                      {counts.succeeded}/{total} done
                    </span>
                  ) : null}
                  {job.completedAt !== undefined ? (
                    <span>· {formatRelativeTime(job.completedAt)}</span>
                  ) : null}
                </>
              ) : null}

              {/* Pending / canceled / interrupted: minimal */}
              {(job.status === 'pending' ||
                job.status === 'canceled' ||
                job.status === 'interrupted') &&
              counts &&
              total > 0 ? (
                <span>
                  {counts.succeeded}/{total} done
                  {counts.interrupted > 0 ? (
                    <span className="text-orange-300"> · {counts.interrupted} stuck</span>
                  ) : null}
                </span>
              ) : null}

              {/* Leaf detail when expanded (or is a leaf) */}
              {(open || isLeaf) && !hasChildren ? (
                <>
                  <span>{JOB_KIND_LABEL[job.kind]}</span>
                  {job.usage.inputTokens !== undefined ||
                  job.usage.outputTokens !== undefined ? (
                    <span>
                      {job.usage.inputTokens ?? 0}in / {job.usage.outputTokens ?? 0}out
                    </span>
                  ) : null}
                  {job.usage.predictTimeSec !== undefined ? (
                    <span>{job.usage.predictTimeSec.toFixed(1)}s</span>
                  ) : null}
                </>
              ) : null}
            </div>

            {(canCancel || canRetry) && (
              <div className="shrink-0">
                {canCancel ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => void jobScheduler.cancel(job.id)}
                  >
                    Cancel
                  </Button>
                ) : null}
                {canRetry ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => void jobScheduler.retry(job.id)}
                  >
                    Retry
                  </Button>
                ) : null}
              </div>
            )}
          </div>

          {isRunning && total > 0 ? (
            <div className="mt-1 h-0.5 w-full overflow-hidden rounded bg-muted/40">
              <div
                className="h-full bg-amber-300/70 transition-[width] duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {hasChildren && open ? (
        <div className="mt-0.5 space-y-0.5">
          {children.map((c) => (
            <JobTreeNode
              key={c.job.id}
              node={c}
              depth={depth + 1}
              isLeaf={c.children.length === 0}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
